import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Switch,
  Slider,
  Divider,
  FormControlLabel,
  alpha
} from '@mui/material';
import BrandTierManager from '../components/templates/brand/BrandTierManager/BrandTierManager';
import {
  Business as BrandIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
  PhotoCamera as PhotoIcon,
  Star as StarIcon,
  Category as CategoryIcon,
  Layers as TierIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Analytics as SmartScoreIcon,
  Psychology as AIIcon,
  Business,
  Apartment,
  Store,
  Smartphone,
  Hub,
  Language
} from '@mui/icons-material';
import { useBrand } from '../context/BrandContext';
import { useAuth } from '../context/AuthContext';
import { useSystemFeatures } from '../context/SystemFeaturesContext';
import BrandCategorySelector from '../components/templates/admin/BrandCategorySelector';
import api from '../services/api';
import PlatformDialog from '../components/molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../components/molecules/PlatformDialog/PlatformDialogTitle';
import BrandFormDialog from '../components/molecules/BrandFormDialog/BrandFormDialog';
import ConfirmDialog from '../components/molecules/ConfirmDialog/ConfirmDialog';
import {
  operationalElevation,
  operationalFeedback,
  operationalFontSizes,
  operationalMotion,
  operationalRadii,
  operationalSizing,
  operationalStatusColors
} from '../design-system/tokens';

const TierProfileInfo = {
  small: {
    label: 'Pequena',
    description: 'Marca regional ou nichada. Tier 1 para fontes acima de 30 mil seguidores (Social Mass).',
    icon: <Store sx={{ fontSize: '1.8rem' }} />
  },
  medium: {
    label: 'Média',
    description: 'Marca de porte médio ou nacional em expansão. Tier 1 para fontes acima de 150 mil seguidores (Social Mass).',
    icon: <Business sx={{ fontSize: '1.8rem' }} />
  },
  large: {
    label: 'Grande',
    description: 'Marca líder de mercado ou multinacional. Tier 1 para fontes acima de 600 mil seguidores (Social Mass).',
    icon: <Apartment sx={{ fontSize: '1.8rem' }} />
  }
};

const SMART_SCORE_DEFAULT_SETTINGS = {
  version: 'mensure-v1',
  neutralPolarity: 0,
  targetScore: 6.1,
  indicators: {
    sentiment: { enabled: true, weight: 22 },
    exposure: { enabled: true, weight: 18 },
    tier: { enabled: true, weight: 18 },
    reach: { enabled: true, weight: 14 },
    editorialOccupancy: { enabled: true, weight: 10 },
    highlight: { enabled: true, weight: 8 },
    spokesperson: { enabled: true, weight: 5 },
    keyMessage: { enabled: true, weight: 5 },
    editorialOrigin: { enabled: false, weight: 0 }
  }
};

const SMART_SCORE_INDICATORS = [
  {
    key: 'sentiment',
    label: 'Sentimento',
    group: 'Mensuracao Editorial',
    description: 'Define se o score vai para o polo positivo, negativo ou neutro.'
  },
  {
    key: 'exposure',
    label: 'Exposicao',
    group: 'Mensuracao Editorial',
    description: 'Dedicada, compartilhada, citacao ou sem exposicao.'
  },
  {
    key: 'tier',
    label: 'Tier',
    group: 'Qualificacao da Fonte',
    description: 'Importancia estrategica da fonte para a marca.'
  },
  {
    key: 'reach',
    label: 'Abrangencia',
    group: 'Qualificacao da Fonte',
    description: 'Nacional, regional ou local.'
  },
  {
    key: 'editorialOccupancy',
    label: 'Ocupacao Editorial',
    group: 'Mensuracao Editorial',
    description: 'Percentual de espaco editorial atribuido a marca.'
  },
  {
    key: 'highlight',
    label: 'Highlight',
    group: 'Mensuracao Editorial',
    description: 'Presenca da marca em posicao de destaque, como titulo.'
  },
  {
    key: 'spokesperson',
    label: 'Porta-voz',
    group: 'Mensuracao Editorial',
    description: 'Porta-voz cadastrado identificado no conteudo.'
  },
  {
    key: 'keyMessage',
    label: 'Mensagem-chave',
    group: 'Mensuracao Editorial',
    description: 'Mensagem-chave cadastrada identificada no conteudo.'
  },
  {
    key: 'editorialOrigin',
    label: 'Origem Editorial',
    group: 'Qualificacao da Fonte',
    description: 'Espontaneo, provocado por release ou provavelmente provocado.'
  }
];

const mergeSmartScoreSettings = (settings = {}) => ({
  ...SMART_SCORE_DEFAULT_SETTINGS,
  ...settings,
  targetScore: typeof settings.targetScore === 'number' ? settings.targetScore : SMART_SCORE_DEFAULT_SETTINGS.targetScore,
  indicators: Object.entries(SMART_SCORE_DEFAULT_SETTINGS.indicators).reduce((acc, [key, defaults]) => {
    acc[key] = {
      ...defaults,
      ...(settings.indicators?.[key] || {})
    };
    return acc;
  }, {})
});

const SMART_SCORE_PRESETS = {
  balanced: {
    label: 'Mensure Padrão',
    description: 'Equilibra reputação, fonte e presença editorial. Melhor opção para começar.',
    settings: SMART_SCORE_DEFAULT_SETTINGS
  },
  institutional: {
    label: 'Institucional',
    description: 'Ideal para reputação corporativa, autoridade da marca e presença em fontes relevantes.',
    settings: mergeSmartScoreSettings({
      indicators: {
        sentiment: { enabled: true, weight: 28 },
        exposure: { enabled: true, weight: 22 },
        tier: { enabled: true, weight: 20 },
        reach: { enabled: true, weight: 10 },
        editorialOccupancy: { enabled: true, weight: 8 },
        highlight: { enabled: true, weight: 6 },
        spokesperson: { enabled: true, weight: 3 },
        keyMessage: { enabled: true, weight: 3 },
        editorialOrigin: { enabled: false, weight: 0 }
      }
    })
  },
  product: {
    label: 'Produto',
    description: 'Valoriza destaque, exposição dedicada e presença clara da marca/produto na matéria.',
    settings: mergeSmartScoreSettings({
      indicators: {
        sentiment: { enabled: true, weight: 20 },
        exposure: { enabled: true, weight: 24 },
        tier: { enabled: true, weight: 14 },
        reach: { enabled: true, weight: 10 },
        editorialOccupancy: { enabled: true, weight: 16 },
        highlight: { enabled: true, weight: 10 },
        spokesperson: { enabled: true, weight: 3 },
        keyMessage: { enabled: true, weight: 3 },
        editorialOrigin: { enabled: false, weight: 0 }
      }
    })
  },
  campaign: {
    label: 'Ação/Campanha',
    description: 'Bom para medir entrega de pauta, mensagens-chave, porta-voz e sinal de provocação.',
    settings: mergeSmartScoreSettings({
      indicators: {
        sentiment: { enabled: true, weight: 18 },
        exposure: { enabled: true, weight: 16 },
        tier: { enabled: true, weight: 12 },
        reach: { enabled: true, weight: 10 },
        editorialOccupancy: { enabled: true, weight: 8 },
        highlight: { enabled: true, weight: 6 },
        spokesperson: { enabled: true, weight: 10 },
        keyMessage: { enabled: true, weight: 10 },
        editorialOrigin: { enabled: true, weight: 10 }
      }
    })
  },
  agency: {
    label: 'Atuação da Agência',
    description: 'Valoriza sinais de trabalho provocado, como release, porta-voz e mensagem-chave.',
    settings: mergeSmartScoreSettings({
      indicators: {
        sentiment: { enabled: true, weight: 20 },
        exposure: { enabled: true, weight: 16 },
        tier: { enabled: true, weight: 15 },
        reach: { enabled: true, weight: 10 },
        editorialOccupancy: { enabled: true, weight: 8 },
        highlight: { enabled: true, weight: 6 },
        spokesperson: { enabled: true, weight: 8 },
        keyMessage: { enabled: true, weight: 8 },
        editorialOrigin: { enabled: true, weight: 9 }
      }
    })
  },
  crisis: {
    label: 'Crise/Reputação Sensível',
    description: 'Dá mais peso ao sentimento e à força da fonte quando uma notícia negativa importa muito.',
    settings: mergeSmartScoreSettings({
      indicators: {
        sentiment: { enabled: true, weight: 32 },
        exposure: { enabled: true, weight: 14 },
        tier: { enabled: true, weight: 22 },
        reach: { enabled: true, weight: 16 },
        editorialOccupancy: { enabled: true, weight: 8 },
        highlight: { enabled: true, weight: 4 },
        spokesperson: { enabled: true, weight: 2 },
        keyMessage: { enabled: true, weight: 2 },
        editorialOrigin: { enabled: false, weight: 0 }
      }
    })
  }
};

const getSmartScorePresetKey = (settings) => {
  const merged = mergeSmartScoreSettings(settings);
  const match = Object.entries(SMART_SCORE_PRESETS).find(([, preset]) => (
    SMART_SCORE_INDICATORS.every((indicator) => {
      const current = merged.indicators[indicator.key] || {};
      const presetCurrent = mergeSmartScoreSettings(preset.settings).indicators[indicator.key] || {};
      return !!current.enabled === !!presetCurrent.enabled && Number(current.weight || 0) === Number(presetCurrent.weight || 0);
    })
  ));
  return match?.[0] || 'custom';
};

const getSmartScoreStatusForScore = (score) => {
  const value = Number(score || 0);
  if (value >= 9.1) return 'Excelente';
  if (value >= 6.1) return 'Muito Bom';
  if (value >= 4.1) return 'Bom';
  if (value >= 2.1) return 'Regular';
  if (value >= -2) return 'Alerta';
  if (value >= -4) return 'Irregular';
  if (value >= -6) return 'Ruim';
  if (value >= -9) return 'Muito Ruim';
  return 'Pessimo';
};

const AI_PROVIDER_OPTIONS = [
  {
    value: 'system-default',
    label: 'Padrao do sistema',
    description: 'Mantem a regra tecnica atual do Mensure.'
  },
  {
    value: 'gemini-free',
    label: 'Gemini gratis',
    description: 'Usa o pool gratuito de chaves Gemini.'
  },
  {
    value: 'gemini-paid',
    label: 'Gemini pago',
    description: 'Usa a chave paga para dar prioridade a esta marca.'
  },
  {
    value: 'deepseek',
    label: 'DeepSeek',
    description: 'Tenta DeepSeek primeiro quando o servico for texto.'
  },
  {
    value: 'disabled',
    label: 'Desativado',
    description: 'Nao usa IA neste servico para esta marca.'
  }
];

const AI_SERVICE_OPTIONS = [
  {
    key: 'qualitativeAnalysis',
    label: 'Analise qualitativa',
    description: 'Verso detalhado do artigo, entidades, sentimento e mensagens.'
  },
  {
    key: 'exposureAnalysis',
    label: 'Exposicao por IA',
    description: 'Botao rapido de exposicao: dedicada, compartilhada, citacao ou sem exposicao.'
  },
  {
    key: 'sentimentAnalysis',
    label: 'Sentimento por IA',
    description: 'Classificacao assistida quando a analise nativa nao basta.'
  },
  {
    key: 'briefingAssistant',
    label: 'Assistente de briefing',
    description: 'Sugestoes de descricao, palavras-chave e produtos.'
  }
];

const AI_SERVICE_DEFAULT_SETTINGS = {
  defaultProvider: 'system-default',
  services: AI_SERVICE_OPTIONS.reduce((acc, service) => {
    acc[service.key] = { provider: 'system-default' };
    return acc;
  }, {})
};

const mergeAIServiceSettings = (settings = {}) => ({
  ...AI_SERVICE_DEFAULT_SETTINGS,
  ...settings,
  services: AI_SERVICE_OPTIONS.reduce((acc, service) => {
    acc[service.key] = {
      provider: settings.services?.[service.key]?.provider || 'system-default'
    };
    return acc;
  }, {})
});

const BrandSettingsPage = () => {
  const navigate = useNavigate();
  const { activeBrandData, updateBrand, setDefaultBrand, createBrand, deleteBrand, brands } = useBrand();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tierProfile, setTierProfile] = useState('medium');
  const [relevantCategories, setRelevantCategories] = useState([]);
  const [trackingMonitorNames, setTrackingMonitorNames] = useState([]);
  const [newMonitorName, setNewMonitorName] = useState('');
  const [powerBiUrl, setPowerBiUrl] = useState('');
  const [logo, setLogo] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#FF5722');
  const [dashboardSlug, setDashboardSlug] = useState('');
  const [smartScoreSettings, setSmartScoreSettings] = useState(SMART_SCORE_DEFAULT_SETTINGS);
  const [smartScorePreset, setSmartScorePreset] = useState('balanced');
  const [aiServiceSettings, setAIServiceSettings] = useState(AI_SERVICE_DEFAULT_SETTINGS);
  // Módulos contratados por marca (null = herda do global)
  const [brandFeatures, setBrandFeatures] = useState({
    smartScore: null,
    aiQualitativeAnalysis: null,
    shareOfVoice: null,
    briefingAssistant: null,
    newsletter: null,
    publicDashboard: null,
    powerBi: null,
  });

  const { features: globalFeatures } = useSystemFeatures();

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteTokenId, setDeleteTokenId] = useState(null);
  const [deleteTokenName, setDeleteTokenName] = useState('');
  const [deleteTokenDialogOpen, setDeleteTokenDialogOpen] = useState(false);

  // Preferências locais do usuário
  const [preferences, setPreferences] = useState({
    tagPropagation: 'ask',
    sentimentPropagation: 'ask'
  });

  // Links públicos do dashboard
  const [shareTokens, setShareTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [createTokenDialog, setCreateTokenDialog] = useState(false);
  const [newTokenData, setNewTokenData] = useState({ name: '', description: '', expiresAt: '', pin: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!isAdmin && activeTab > 1) {
      setActiveTab(0);
    }
  }, [activeTab, isAdmin]);

  // Carregar tokens de compartilhamento
  useEffect(() => {
    if (activeBrandData?._id) {
      loadShareTokens();
    }
  }, [activeBrandData?._id]);

  const loadShareTokens = async () => {
    try {
      setTokensLoading(true);
      const brandId = localStorage.getItem('activeBrandId');
      console.log('[BrandSettings] Carregando tokens para brandId:', brandId);
      const response = await api.get('/brands/share-tokens');
      console.log('[BrandSettings] Tokens recebidos:', response.data.data);
      setShareTokens(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar tokens:', error);
    } finally {
      setTokensLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const emptyTokenData = { name: '', description: '', expiresAt: '', pin: '' };

  const openCreateTokenDialog = () => {
    setNewTokenData(emptyTokenData);
    setCreateTokenDialog(true);
  };

  const closeCreateTokenDialog = () => {
    setCreateTokenDialog(false);
    setNewTokenData(emptyTokenData);
  };

  const handleCreateToken = async () => {
    if (!newTokenData.name.trim()) {
      showSnackbar('Nome é obrigatório', 'error');
      return;
    }
    try {
      setTokensLoading(true);
      const response = await api.post('/brands/share-tokens', {
        name: newTokenData.name.trim(),
        description: newTokenData.description,
        expiresAt: newTokenData.expiresAt || null,
        pin: newTokenData.pin || null
      });
      setShareTokens([response.data.data, ...shareTokens]);
      closeCreateTokenDialog();
      showSnackbar('Link criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar token:', error);
      showSnackbar(error.response?.data?.message || 'Erro ao criar link', 'error');
    } finally {
      setTokensLoading(false);
    }
  };

  const handleDeleteToken = (tokenId) => {
    const token = shareTokens.find(t => t._id === tokenId);
    setDeleteTokenId(tokenId);
    setDeleteTokenName(token?.name || '');
    setDeleteTokenDialogOpen(true);
  };

  const confirmDeleteToken = async () => {
    try {
      await api.delete(`/brands/share-tokens/${deleteTokenId}`);
      setShareTokens(shareTokens.filter(t => t._id !== deleteTokenId));
      setDeleteTokenDialogOpen(false);
      showSnackbar('Link excluído com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir token:', error);
      showSnackbar('Erro ao excluir link', 'error');
    }
  };

  const handleToggleToken = async (token) => {
    try {
      const response = await api.put(`/brands/share-tokens/${token._id}`, {
        isActive: !token.isActive
      });
      setShareTokens(shareTokens.map(t => t._id === token._id ? response.data.data : t));
      showSnackbar(token.isActive ? 'Link desativado' : 'Link ativado');
    } catch (error) {
      console.error('Erro ao atualizar token:', error);
      showSnackbar('Erro ao atualizar link', 'error');
    }
  };

  const [regenerateTokenId, setRegenerateTokenId] = useState(null);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);

  const handleRegenerateToken = async (token) => {
    setRegenerateTokenId(token._id);
    setRegenerateDialogOpen(true);
  };

  const confirmRegenerateToken = async () => {
    try {
      const response = await api.post(`/brands/share-tokens/${regenerateTokenId}/regenerate`);
      setShareTokens(shareTokens.map(t => t._id === regenerateTokenId ? response.data.data : t));
      setRegenerateDialogOpen(false);
      showSnackbar('Link regenerado com sucesso!');
    } catch (error) {
      console.error('Erro ao regenerar token:', error);
      showSnackbar('Erro ao regenerar link', 'error');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Link copiado!');
  };

  // Load local preferences
  useEffect(() => {
    const tagPref = localStorage.getItem('tagPropagation_preference') || 'ask';
    const sentimentPref = localStorage.getItem('sentimentPropagation_preference') || 'ask';
    setPreferences({
      tagPropagation: tagPref,
      sentimentPropagation: sentimentPref
    });
  }, []);

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    const storageKey = key === 'tagPropagation' 
      ? 'tagPropagation_preference' 
      : 'sentimentPropagation_preference';
    localStorage.setItem(storageKey, value);
    setSuccess('Preferência salva com sucesso!');
    setTimeout(() => setSuccess(''), 3000);
  };

  // Load brand data
  useEffect(() => {
    if (activeBrandData) {
      setName(activeBrandData.name || '');
      setDescription(activeBrandData.description || '');
      setTierProfile(activeBrandData.tierProfile || 'medium');
      setRelevantCategories(activeBrandData.relevantCategories || []);
      setTrackingMonitorNames(activeBrandData.trackingMonitorNames || []);
      setPowerBiUrl(activeBrandData.powerBiUrl || '');
      setLogo(activeBrandData.logo || '');
      setPrimaryColor(activeBrandData.primaryColor || '#FF5722');
      setDashboardSlug(activeBrandData.dashboardSlug || '');
      const mergedSettings = mergeSmartScoreSettings(activeBrandData.smartScoreSettings);
      setSmartScoreSettings(mergedSettings);
      setSmartScorePreset(getSmartScorePresetKey(mergedSettings));
      setAIServiceSettings(mergeAIServiceSettings(activeBrandData.aiServiceSettings));
      // Carregar features por marca (null = herda global)
      setBrandFeatures(prev => ({
        ...prev,
        ...(activeBrandData.features || {})
      }));
    }
  }, [activeBrandData]);

  const handleSmartScoreIndicatorChange = (key, patch) => {
    setSmartScorePreset('custom');
    setSmartScoreSettings(prev => ({
      ...prev,
      indicators: {
        ...prev.indicators,
        [key]: {
          ...prev.indicators[key],
          ...patch
        }
      }
    }));
  };

  const handleSmartScoreTargetChange = (value) => {
    setSmartScoreSettings(prev => ({
      ...prev,
      targetScore: Math.max(-10, Math.min(10, Number(value || 0)))
    }));
  };

  const resetSmartScorePreset = () => {
    setSmartScoreSettings(SMART_SCORE_DEFAULT_SETTINGS);
    setSmartScorePreset('balanced');
  };

  const applySmartScorePreset = (presetKey) => {
    const preset = SMART_SCORE_PRESETS[presetKey];
    if (!preset) return;
    setSmartScorePreset(presetKey);
    setSmartScoreSettings(mergeSmartScoreSettings(preset.settings));
  };

  const getSmartScoreActiveWeightTotal = () => (
    Object.values(smartScoreSettings.indicators || {}).reduce((sum, indicator) => (
      indicator?.enabled ? sum + Number(indicator.weight || 0) : sum
    ), 0)
  );

  const getSmartScoreWeightShare = (indicator) => {
    const total = getSmartScoreActiveWeightTotal();
    if (!indicator?.enabled || total <= 0) return 0;
    return Math.round((Number(indicator.weight || 0) / total) * 100);
  };

  const handleAIProviderChange = (serviceKey, provider) => {
    setAIServiceSettings(prev => ({
      ...prev,
      services: {
        ...prev.services,
        [serviceKey]: {
          ...(prev.services?.[serviceKey] || {}),
          provider
        }
      }
    }));
  };

  const handleAIDefaultProviderChange = (provider) => {
    setAIServiceSettings(prev => ({
      ...prev,
      defaultProvider: provider
    }));
  };

  const handleAddMonitorName = () => {
    const trimmed = newMonitorName.trim();
    if (trimmed && !trackingMonitorNames.includes(trimmed)) {
      setTrackingMonitorNames([...trackingMonitorNames, trimmed]);
      setNewMonitorName('');
    }
  };

  const handleRemoveMonitorName = (nameToRemove) => {
    setTrackingMonitorNames(trackingMonitorNames.filter(n => n !== nameToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddMonitorName();
    }
  };

  const handleSave = async () => {
    if (!activeBrandData?._id) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await updateBrand(activeBrandData._id, {
        name,
        logo,
        primaryColor,
        dashboardSlug,
        description,
        tierProfile,
        trackingMonitorNames: isAdmin ? trackingMonitorNames : undefined,
        powerBiUrl,
        ...(isAdmin ? {
          smartScoreSettings,
          aiServiceSettings,
          features: brandFeatures
        } : {})
      });

      if (result.success) {
        await loadShareTokens();
        setSuccess('Configuracoes salvas com sucesso!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Erro ao salvar');
      }
    } catch (err) {
      setError('Erro ao salvar configuracoes');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async () => {
    if (!activeBrandData?._id) return;

    setLoading(true);
    try {
      const result = await setDefaultBrand(activeBrandData._id);
      if (result.success) {
        setSuccess('Marca definida como padrao!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Erro ao definir padrao');
      }
    } catch (err) {
      setError('Erro ao definir como padrao');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryUpdate = (updatedBrand) => {
    if (updatedBrand?.relevantCategories) {
      setRelevantCategories(updatedBrand.relevantCategories);
    }
  };

  // Create new brand
  const handleCreateBrand = async (formData) => {
    setLoading(true);
    setError('');

    try {
      const result = await createBrand({
        name: formData.name.trim(),
        tierProfile: formData.tierProfile
      });

      if (result.success) {
        setSuccess('Marca criada com sucesso!');
        setCreateDialogOpen(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Erro ao criar marca');
      }
    } catch (err) {
      setError('Erro ao criar marca');
    } finally {
      setLoading(false);
    }
  };

  // Delete brand
  const handleDeleteBrand = async () => {
    if (!activeBrandData?._id || deleteConfirmName !== activeBrandData.name) return;

    setLoading(true);
    setError('');

    try {
      const result = await deleteBrand(activeBrandData._id);

      if (result.success) {
        setDeleteDialogOpen(false);
        setDeleteConfirmName('');
        navigate('/brand-selection');
      } else {
        setError(result.message || 'Erro ao excluir marca');
      }
    } catch (err) {
      setError('Erro ao excluir marca');
    } finally {
      setLoading(false);
    }
  };

  const surfaceSx = {
    borderRadius: `${operationalRadii.surface}px`,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: operationalElevation.none
  };

  const innerSurfaceSx = {
    ...surfaceSx,
    bgcolor: 'background.default'
  };

  const buttonSx = {
    minHeight: operationalSizing.compact,
    borderRadius: `${operationalRadii.control}px`,
    fontWeight: 650,
    textTransform: 'none',
    boxShadow: operationalElevation.none,
    '&:hover': {
      boxShadow: operationalElevation.none
    }
  };

  const alertSx = (severity = 'info') => ({
    borderRadius: `${operationalRadii.control}px`,
    border: '1px solid',
    borderLeft: `${operationalFeedback.alert.accentBorderWidth}px solid ${operationalStatusColors[severity] || operationalStatusColors.info}`,
    borderColor: 'divider',
    bgcolor: 'background.paper',
    color: 'text.primary',
    boxShadow: operationalElevation.none,
    '& .MuiAlert-icon': {
      color: operationalStatusColors[severity] || operationalStatusColors.info
    }
  });

  const tabSx = {
    minHeight: 44,
    px: 2,
    color: 'text.secondary',
    fontSize: operationalFontSizes.sm,
    fontWeight: 700,
    letterSpacing: 0,
    textTransform: 'none',
    '&.Mui-selected': {
      color: 'text.primary'
    }
  };

  const settingsRootSx = {
    p: 3,
    maxWidth: 1400,
    mx: 'auto',
    '& .MuiCard-root, & .MuiPaper-root': {
      borderRadius: `${operationalRadii.surface}px`,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: operationalElevation.none
    },
    '& .MuiButton-root': {
      borderRadius: `${operationalRadii.control}px`,
      textTransform: 'none',
      boxShadow: operationalElevation.none
    },
    '& .MuiButton-root:hover': {
      boxShadow: operationalElevation.none
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: `${operationalRadii.control}px`
    },
    '& .MuiChip-root': {
      borderRadius: operationalRadii.pill
    },
    '& .MuiAlert-root': {
      borderRadius: `${operationalRadii.control}px`
    },
    '& .MuiToggleButton-root': {
      borderRadius: `${operationalRadii.control}px !important`,
      textTransform: 'none',
      transition: `background-color ${operationalMotion.quick}, border-color ${operationalMotion.quick}`
    }
  };

  if (!activeBrandData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Nenhuma marca selecionada. Selecione uma marca primeiro.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/brand-selection')}
          sx={{ mt: 2 }}
        >
          Selecionar Marca
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={settingsRootSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ width: 36, height: 36 }}>
          <BackIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {activeBrandData.logo ? (
            <Avatar src={activeBrandData.logo} sx={{ width: 40, height: 40, borderRadius: `${operationalRadii.control}px` }} />
          ) : (
            <Avatar sx={{ width: 40, height: 40, borderRadius: `${operationalRadii.control}px`, bgcolor: 'primary.main' }}>
              <BrandIcon />
            </Avatar>
          )}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0, lineHeight: 1.15 }}>
              Configuracoes da Marca
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeBrandData.name}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeBrandData.isDefault && (
            <Chip
              icon={<StarIcon sx={{ fontSize: '0.875rem !important' }} />}
              label="Marca Padrao"
              color="primary"
              size="small"
              sx={{ fontWeight: 650 }}
            />
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={buttonSx}
          >
            Nova Marca
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ ...alertSx('error'), mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ ...alertSx('success'), mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Navegação por Abas */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            minHeight: 44,
            '& .MuiTab-root': tabSx,
            '& .MuiTabs-indicator': {
              height: 2,
              borderRadius: operationalRadii.pill
            }
          }}
        >
          <Tab label="Configurações Gerais" />
          <Tab label="Ajuste Fino de Tiers" />
          {isAdmin && <Tab label="SmartScore" />}
          {isAdmin && <Tab label="IA por Servico" />}
          {isAdmin && <Tab label="Módulos" />}
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Grid container spacing={3}>
        {/* Informacoes Basicas */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BrandIcon fontSize="small" color="primary" />
                Informacoes Basicas
              </Typography>

              <TextField
                fullWidth
                label="Nome da Marca"
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
                size="small"
              />

              <TextField
                fullWidth
                label="Logo da Marca"
                value={logo}
                onChange={(e) => setLogo(e.target.value)}
                margin="normal"
                size="small"
                placeholder="URL do logo ou caminho do arquivo"
                helperText="Este logo aparece no dashboard público compartilhado."
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Cor principal da marca
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <TextField
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    size="small"
                    placeholder="#FF5722"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    size="small"
                    sx={{
                      width: 92,
                      '& input': { p: 0.5, height: 40 }
                    }}
                  />
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: primaryColor
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Essa cor será usada no dashboard público e em variações derivadas.
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Descricao"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
                size="small"
                multiline
                rows={2}
                placeholder="Ex: Clipping de midia para Mercedes-Benz Brasil"
              />

              <TextField
                fullWidth
                label="Slug publico do dashboard"
                value={dashboardSlug}
                onChange={(e) => setDashboardSlug(e.target.value)}
                margin="normal"
                size="small"
                placeholder="Ex: bmw, softex, abidip"
                helperText="Usado em links como /dashboard/bmw/TOKEN. Deixe vazio para usar o link antigo sem slug."
              />

              <TextField
                fullWidth
                label="Power BI Dashboard URL"
                value={powerBiUrl}
                onChange={(e) => setPowerBiUrl(e.target.value)}
                margin="normal"
                size="small"
                placeholder="Cole aqui o link do dashboard público do Power BI"
                helperText="Use o link de 'Publicar na Web' do Power BI"
              />

              {isAdmin && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Painéis Externos de Tracking (TrackingSource)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Adicione os nomes exatos dos grupos na fonte externa. A sincronização buscará artigos de TODOS os painéis listados.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      value={newMonitorName}
                      onChange={(e) => setNewMonitorName(e.target.value)}
                      onKeyPress={handleKeyPress}
                      size="small"
                      placeholder="Ex: MLP_Softex"
                    />
                    <Button 
                      variant="outlined" 
                      onClick={handleAddMonitorName}
                      disabled={!newMonitorName.trim()}
                      size="small"
                      startIcon={<AddIcon />}
                    >
                      Adicionar
                    </Button>
                  </Box>
                  
                  {trackingMonitorNames.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {trackingMonitorNames.map((monitor, index) => (
                        <Chip
                          key={index}
                          label={monitor}
                          onDelete={() => handleRemoveMonitorName(monitor)}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Nenhum painel configurado. Adicione pelo menos um para habilitar a Sincronização Automática.
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={loading || !name.trim()}
                  size="small"
                >
                  Salvar
                </Button>
                {!activeBrandData.isDefault && (
                  <Button
                    variant="outlined"
                    startIcon={<StarIcon />}
                    onClick={handleSetDefault}
                    disabled={loading}
                    size="small"
                  >
                    Definir como Padrao
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Perfil de Tier */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TierIcon fontSize="small" color="primary" />
                Perfil de Tier (Tamanho)
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Define os limites de seguidores para cada tier
              </Typography>

              <ToggleButtonGroup
                value={tierProfile}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue) setTierProfile(newValue);
                }}
                fullWidth
                size="small"
              >
                {Object.entries(TierProfileInfo).map(([key, info]) => (
                  <ToggleButton key={key} value={key} sx={{ flexDirection: 'column', py: 2 }}>
                    <Box sx={{ mb: 1, color: tierProfile === key ? 'primary.main' : 'text.secondary' }}>
                      {key === 'small' ? <Store sx={{ fontSize: '2rem' }} /> : key === 'medium' ? <Business sx={{ fontSize: '2rem' }} /> : <Apartment sx={{ fontSize: '2rem' }} />}
                    </Box>
                    <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: '0.05em' }}>
                      {info.label.toUpperCase()}
                    </Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {tierProfile && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="caption" fontWeight={700} color="primary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase' }}>
                    Impacto Estimado ({TierProfileInfo[tierProfile]?.label})
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      ...innerSurfaceSx,
                      p: 0, 
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.selected' }}>
                       <Typography variant="body2" fontWeight={500}>
                         {TierProfileInfo[tierProfile]?.description}
                       </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        {[
                          { 
                            group: 'Social Mass', 
                            t1: tierProfile === 'small' ? '30k' : tierProfile === 'medium' ? '150k' : '600k',
                            t2: tierProfile === 'small' ? '10k' : tierProfile === 'medium' ? '50k' : '200k',
                            icon: <Smartphone /> 
                          },
                          { 
                            group: 'Social Info', 
                            t1: tierProfile === 'small' ? '6k' : tierProfile === 'medium' ? '30k' : '120k',
                            t2: tierProfile === 'small' ? '2k' : tierProfile === 'medium' ? '10k' : '40k',
                            icon: <Hub /> 
                          },
                          { 
                            group: 'Online/Portais', 
                            t1: tierProfile === 'small' ? '3k' : tierProfile === 'medium' ? '15k' : '60k',
                            t2: tierProfile === 'small' ? '1k' : tierProfile === 'medium' ? '5k' : '20k',
                            icon: <Language /> 
                          }
                        ].map((ref, idx) => (
                          <Grid item xs={4} key={idx}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Box sx={{ mb: 1, color: 'primary.main' }}>
                                {React.cloneElement(ref.icon, { sx: { fontSize: '1.6rem' } })}
                              </Box>
                              <Typography variant="caption" color="text.primary" sx={{ display: 'block', fontWeight: 800, mb: 1, fontSize: '0.75rem' }}>
                                {ref.group}
                              </Typography>
                              
                              <Box sx={{ mb: 2 }}>
                                <Typography fontWeight={800} color="success.main" sx={{ lineHeight: 1, fontSize: '1.35rem', letterSpacing: 0 }}>
                                  +{ref.t1}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'success.main', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.9 }}>
                                  Tier 1
                                </Typography>
                              </Box>

                              <Box sx={{ mb: 1.5, opacity: 0.85 }}>
                                <Typography fontWeight={800} color="warning.main" sx={{ lineHeight: 1, fontSize: '1rem' }}>
                                  +{ref.t2}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'warning.main', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>
                                  Tier 2
                                </Typography>
                              </Box>

                              <Box sx={{ opacity: 0.6 }}>
                                <Typography fontWeight={700} color="text.disabled" sx={{ lineHeight: 1, fontSize: '0.8rem' }}>
                                  {'<'} {ref.t2}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                                  Tier 3
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Paper>
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                    * Os valores acima referem-se à audiência/seguidores necessários para atingir o Tier Máximo.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Categorias Relevantes */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon fontSize="small" color="primary" />
                Categorias Relevantes para a Marca
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Fontes nestas categorias recebem <strong>Tier 1</strong> automaticamente, independente do tamanho.
                Exemplo: Mercedes pode definir "Automotivo" como relevante.
              </Typography>

              <BrandCategorySelector
                brand={activeBrandData}
                onUpdate={handleCategoryUpdate}
              />
            </CardContent>
          </Card>
        </Grid>


        {/* Links Públicos do Dashboard */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={surfaceSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon fontSize="small" color="primary" />
                    Links Públicos do Dashboard
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Crie links compartilháveis para permitir que qualquer pessoa visualize o dashboard desta marca
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={openCreateTokenDialog}
                  sx={buttonSx}
                >
                  Criar Link
                </Button>
              </Box>

              {tokensLoading && shareTokens.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : shareTokens.length === 0 ? (
                <Alert severity="info" sx={alertSx('info')}>
                  Nenhum link público criado. Clique em "Criar Link" para gerar um link compartilhável do dashboard.
                </Alert>
              ) : (
                <List dense sx={{ ...innerSurfaceSx, p: 0, overflow: 'hidden' }}>
                  {shareTokens.map((token) => (
                    <ListItem
                      key={token._id}
                      divider
                      sx={{ 
                        opacity: token.isActive ? 1 : 0.5,
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {token.name}
                            </Typography>
                            <Chip 
                              label={token.isActive ? 'Ativo' : 'Inativo'} 
                              size="small" 
                              color={token.isActive ? 'success' : 'default'}
                              onClick={() => handleToggleToken(token)}
                              sx={{ cursor: 'pointer', height: 20, fontSize: '0.7rem' }}
                            />
                            {token.requiresPin && (
                              <Chip
                                label="Com PIN"
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              ...{token.token?.slice(-10)}
                            </Typography>
                            <Chip 
                              label="Copiar" 
                              size="small" 
                              variant="outlined"
                              onClick={() => copyToClipboard(token.shareUrl)}
                              sx={{ height: 18, fontSize: '0.65rem', cursor: 'pointer' }}
                            />
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Regenerar link">
                            <IconButton size="small" onClick={() => handleRegenerateToken(token)}>
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" color="error" onClick={() => handleDeleteToken(token._id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Preferências do Sistema */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={surfaceSx}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon fontSize="small" color="primary" />
                Preferências Locais do Analista
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configurações salvas localmente neste navegador. Elas não afetam outros usuários.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Comportamento das Tags
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    O que fazer quando adicionar tags a um artigo que possui artigos similares aglomerados?
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={preferences.tagPropagation}
                      onChange={(e) => handlePreferenceChange('tagPropagation', e.target.value)}
                    >
                      <MenuItem value="ask">Perguntar sempre (Recomendado)</MenuItem>
                      <MenuItem value="always">Aplicar em todos os similares</MenuItem>
                      <MenuItem value="never">Aplicar apenas no artigo selecionado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Comportamento do Sentimento
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    O que fazer quando o sentimento de um artigo que possui similares for alterado?
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={preferences.sentimentPropagation}
                      onChange={(e) => handlePreferenceChange('sentimentPropagation', e.target.value)}
                    >
                      <MenuItem value="ask">Perguntar sempre (Recomendado)</MenuItem>
                      <MenuItem value="always">Aplicar em todos os similares</MenuItem>
                      <MenuItem value="never">Aplicar apenas no artigo selecionado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Zona de Perigo - Excluir Marca */}
        {brands.length > 1 && (
          <Grid item xs={12}>
            <Card sx={{ borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon fontSize="small" />
                  Zona de Perigo
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Excluir esta marca
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Esta acao e irreversivel. Todos os artigos e configuracoes serao perdidos.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Excluir Marca
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
        </Grid>
      )}

      {activeTab === 1 && (
        <Card variant="outlined" sx={{ ...surfaceSx, p: 3 }}>
          <BrandTierManager />
        </Card>
      )}

      {isAdmin && activeTab === 2 && (
        <Card variant="outlined" sx={surfaceSx}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmartScoreIcon fontSize="small" color="primary" />
                  SmartScore Mensure
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
                  Escolha como esta marca deve interpretar resultado de imprensa. O ajuste fino continua disponivel, mas o caminho normal e usar um preset.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <Button variant="outlined" size="small" onClick={resetSmartScorePreset} sx={buttonSx}>
                  Restaurar Mensure Padrao
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={loading || !name.trim()}
                  sx={buttonSx}
                >
                  Salvar
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ ...alertSx('info'), mb: 3 }}>
              Audiencia, page views e AVE ficam como contexto, nao como peso do SmartScore. Assim uma fonte trade importante nao recebe score baixo so porque tem audiencia menor.
            </Alert>

            <Paper variant="outlined" sx={{ ...innerSurfaceSx, p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Meta do SmartScore
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Define a pontuacao alvo usada no Dashboard para comparar a saude reputacional da marca.
                  </Typography>
                </Box>
                <Chip
                  label={`Status-alvo: ${getSmartScoreStatusForScore(smartScoreSettings.targetScore)}`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 650 }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  size="small"
                  min={-10}
                  max={10}
                  step={0.1}
                  value={Number(smartScoreSettings.targetScore || 6.1)}
                  onChange={(event, value) => handleSmartScoreTargetChange(value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Meta"
                  value={Number(smartScoreSettings.targetScore || 6.1)}
                  onChange={(event) => handleSmartScoreTargetChange(event.target.value)}
                  inputProps={{ min: -10, max: 10, step: 0.1 }}
                  sx={{ width: 110 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Padrao recomendado: +6.1, entrada da faixa Muito Bom.
              </Typography>
            </Paper>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              O que voce quer priorizar?
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {Object.entries(SMART_SCORE_PRESETS).map(([presetKey, preset]) => (
                <Grid item xs={12} md={6} key={presetKey}>
                  <Paper
                    variant="outlined"
                    onClick={() => applySmartScorePreset(presetKey)}
                    sx={{
                      ...innerSurfaceSx,
                      p: 2,
                      height: '100%',
                      cursor: 'pointer',
                      bgcolor: smartScorePreset === presetKey ? 'action.selected' : innerSurfaceSx.bgcolor,
                      borderColor: smartScorePreset === presetKey ? 'text.secondary' : 'divider',
                      transition: `border-color ${operationalMotion.standard}, background-color ${operationalMotion.standard}`
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Typography fontWeight={800}>{preset.label}</Typography>
                      {smartScorePreset === presetKey && (
                        <Chip label="Selecionado" size="small" color="primary" sx={{ fontWeight: 700 }} />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {preset.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
              {smartScorePreset === 'custom' && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={alertSx('warning')}>
                    Esta marca esta usando uma configuracao personalizada.
                  </Alert>
                </Grid>
              )}
            </Grid>

            <Paper variant="outlined" sx={{ ...innerSurfaceSx, p: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Composição atual
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {SMART_SCORE_INDICATORS
                  .filter(indicator => smartScoreSettings.indicators[indicator.key]?.enabled)
                  .map(indicator => (
                    <Chip
                      key={indicator.key}
                      label={`${indicator.label}: ${getSmartScoreWeightShare(smartScoreSettings.indicators[indicator.key])}%`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Esta é a participação real de cada indicador no SmartScore. Ao mover um controle, o sistema recalcula a proporção automaticamente.
              </Typography>
            </Paper>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1.5 }}>
              Ajuste a influência de cada indicador
            </Typography>

            <Grid container spacing={2}>
              {SMART_SCORE_INDICATORS.map((indicator) => {
                const current = smartScoreSettings.indicators[indicator.key] || { enabled: false, weight: 0 };
                const share = getSmartScoreWeightShare(current);

                return (
                  <Grid item xs={12} md={6} key={indicator.key}>
                    <Paper
                      variant="outlined"
                      sx={{
                        ...innerSurfaceSx,
                        p: 1.75,
                        bgcolor: current.enabled ? 'action.selected' : innerSurfaceSx.bgcolor,
                        borderColor: current.enabled ? 'text.secondary' : 'divider'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                          <Typography fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {indicator.label}
                            <Chip
                              label={current.enabled ? `${share}% do SmartScore` : 'fora do score'}
                              size="small"
                              color={current.enabled ? 'primary' : 'default'}
                              variant={current.enabled ? 'filled' : 'outlined'}
                              sx={{ height: 22, fontWeight: 650 }}
                            />
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {indicator.group}
                          </Typography>
                        </Box>
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Switch
                              size="small"
                              checked={!!current.enabled}
                              onChange={(event) => handleSmartScoreIndicatorChange(indicator.key, { enabled: event.target.checked })}
                            />
                          }
                          label=""
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ minHeight: 38, mt: 1.25 }}>
                        {indicator.description}
                      </Typography>

                      <Divider sx={{ my: 1.5 }} />

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Slider
                          size="small"
                          min={0}
                          max={100}
                          step={1}
                          value={Number(current.weight || 0)}
                          disabled={!current.enabled}
                          onChange={(event, value) => handleSmartScoreIndicatorChange(indicator.key, { weight: value })}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          type="number"
                          label="Influencia"
                          value={Number(current.weight || 0)}
                          disabled={!current.enabled}
                          onChange={(event) => {
                            const value = Math.max(0, Math.min(100, Number(event.target.value || 0)));
                            handleSmartScoreIndicatorChange(indicator.key, { weight: value });
                          }}
                          inputProps={{ min: 0, max: 100 }}
                          sx={{ width: 96 }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Use valores maiores para fazer este indicador pesar mais na nota final.
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {isAdmin && activeTab === 3 && (
        <Card variant="outlined" sx={surfaceSx}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AIIcon fontSize="small" color="primary" />
                  IA por servico
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
                  Escolha quando esta marca usa IA gratis, paga ou outro provedor. Servicos em "Padrao do sistema" continuam seguindo a regra global atual.
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={loading || !name.trim()}
                sx={buttonSx}
              >
                Salvar
              </Button>
            </Box>

            <Alert severity="info" sx={{ ...alertSx('info'), mb: 3 }}>
              Use "Gemini pago" para marcas prioritarias ou servicos criticos, como analise qualitativa. O restante pode continuar no plano gratis ou no padrao do sistema.
            </Alert>

            <Paper variant="outlined" sx={{ ...innerSurfaceSx, p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography fontWeight={800}>Padrao da marca</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Usado quando um servico abaixo estiver como "Padrao do sistema".
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={aiServiceSettings.defaultProvider || 'system-default'}
                      onChange={(event) => handleAIDefaultProviderChange(event.target.value)}
                    >
                      {AI_PROVIDER_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            <Grid container spacing={2}>
              {AI_SERVICE_OPTIONS.map(service => {
                const provider = aiServiceSettings.services?.[service.key]?.provider || 'system-default';
                const providerInfo = AI_PROVIDER_OPTIONS.find(option => option.value === provider);

                return (
                  <Grid item xs={12} md={6} key={service.key}>
                    <Paper variant="outlined" sx={{ ...innerSurfaceSx, p: 2, height: '100%' }}>
                      <Typography fontWeight={800}>{service.label}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40, mt: 0.5 }}>
                        {service.description}
                      </Typography>
                      <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                        <Select
                          value={provider}
                          onChange={(event) => handleAIProviderChange(service.key, event.target.value)}
                        >
                          {AI_PROVIDER_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {providerInfo?.description}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ── ABA 4: MÓDULOS CONTRATADOS POR MARCA ────────────────────────────── */}
      {isAdmin && activeTab === 4 && (
        <Card variant="outlined" sx={surfaceSx}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  🧩 Módulos Contratados
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
                  Controle quais módulos estão disponíveis <strong>especificamente para esta marca</strong>.
                  O módulo global precisa estar ativo para que o override da marca tenha efeito.
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={loading || !name.trim()}
                sx={{ ...buttonSx, flexShrink: 0 }}
              >
                Salvar
              </Button>
            </Box>

            <Alert severity="info" sx={{ ...alertSx('info'), mb: 3 }}>
              <strong>Hierarquia:</strong> Global (master) → Marca (override). Se o módulo estiver desabilitado globalmente, o override da marca não tem efeito.
            </Alert>

            {[
              { key: 'smartScore',            label: 'SmartScore',              icon: '📊', desc: 'Pontuação editorial -10 a +10 por artigo.' },
              { key: 'aiQualitativeAnalysis', label: 'Análise Qualitativa IA',  icon: '🧠', desc: 'KPIA completo: exposição, sentimento, mensagens-chave, porta-vozes.' },
              { key: 'shareOfVoice',          label: 'Share of Voice',          icon: '📈', desc: 'Comparativo da marca vs. concorrentes no dashboard.' },
              { key: 'briefingAssistant',     label: 'Assistente de Briefing',  icon: '✍️', desc: 'Sugestões automáticas de IA ao configurar a marca.' },
              { key: 'newsletter',            label: 'Newsletter Editorial',    icon: '📩', desc: 'Criação e envio de newsletters editoriais.' },
              { key: 'publicDashboard',       label: 'Dashboard Compartilhado', icon: '🔗', desc: 'Links públicos para compartilhamento do dashboard.' },
              { key: 'powerBi',              label: 'Power BI Analytics',      icon: '📉', desc: 'Integração com relatórios Power BI.' },
            ].map(module => {
              const globalOn = globalFeatures[module.key] !== false;
              const brandVal = brandFeatures[module.key]; // true | false | null
              const effective = !globalOn ? false : (brandVal === false ? false : true);

              // Cores sem usar color="default" (não existe no tema admin)
              const BTN_STYLES = {
                null:  { active: { bgcolor: '#546e7a', color: '#fff', borderColor: '#546e7a' }, idle: { color: '#546e7a', borderColor: '#546e7a' } },
                true:  { active: { bgcolor: 'success.main', color: '#fff', borderColor: 'success.main' }, idle: { color: 'success.main', borderColor: 'success.main' } },
                false: { active: { bgcolor: 'error.main', color: '#fff', borderColor: 'error.main' }, idle: { color: 'error.main', borderColor: 'error.main' } },
              };

              return (
                <Paper key={module.key} variant="outlined" sx={{ ...innerSurfaceSx, p: 2, mb: 1.5, opacity: globalOn ? 1 : 0.55, borderColor: effective ? 'success.main' : 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '1rem' }}>{module.icon}</Typography>
                        <Typography fontWeight={700}>{module.label}</Typography>
                        {/* Status global — sem color="default" */}
                        <Chip
                          size="small"
                          label={globalOn ? 'Global: Ativo' : 'Global: Off'}
                          variant="outlined"
                          sx={{
                            fontSize: '0.62rem', height: 18,
                            color: globalOn ? 'success.main' : '#90a4ae',
                            borderColor: globalOn ? 'success.main' : '#90a4ae',
                          }}
                        />
                        {/* Status efetivo */}
                        <Chip
                          size="small"
                          label={effective ? '✓ Ativo' : '✗ Bloqueado'}
                          sx={{
                            fontSize: '0.62rem', height: 18,
                            bgcolor: effective ? 'success.main' : 'error.main',
                            color: '#fff',
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">{module.desc}</Typography>
                    </Box>

                    {/* Controle triplo: Herdar (—) | ON | OFF */}
                    <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                      {[
                        { val: null,  label: 'Herdar global' },
                        { val: true,  label: 'Forçar ativo'  },
                        { val: false, label: 'Desabilitar'   },
                      ].map(opt => {
                        const isActive = brandVal === opt.val;
                        const styles = BTN_STYLES[String(opt.val)];
                        return (
                          <Tooltip key={String(opt.val)} title={opt.label} arrow>
                            <span>
                              <Button
                                size="small"
                                variant={isActive ? 'contained' : 'outlined'}
                                disabled={!globalOn && opt.val === true}
                                onClick={() => setBrandFeatures(prev => ({ ...prev, [module.key]: opt.val }))}
                                sx={{
                                  minWidth: 0, px: 1.5, py: 0.4,
                                  fontSize: '0.7rem', fontWeight: 650,
                                  ...(isActive ? styles.active : styles.idle),
                                  '&:hover': styles.active,
                                }}
                              >
                                {opt.val === null ? '—' : opt.val ? 'ON' : 'OFF'}
                              </Button>
                            </span>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Dialog - Criar Nova Marca (Padronizado) */}
      <BrandFormDialog

        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateBrand}
        isCreating={loading}
        isAdminOrMensure={false} // Nesta página, cria para si mesmo
      />

      {/* Dialog - Confirmar Exclusao (Padronizado) */}
      <PlatformDialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
      >
        <PlatformDialogTitle
          title="Excluir Marca"
          subtitle="Esta ação é irreversível e apagará todos os dados."
          icon={<WarningIcon />}
          onClose={() => setDeleteDialogOpen(false)}
          color="error"
        />
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            Todos os artigos e configurações da marca serão excluídos permanentemente.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Para confirmar, digite o nome da marca: <strong>{activeBrandData?.name}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Nome da marca para confirmar"
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            placeholder={activeBrandData?.name}
            error={deleteConfirmName.length > 0 && deleteConfirmName !== activeBrandData?.name}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button 
            onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmName(''); }}
            sx={{ fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteBrand}
            disabled={loading || deleteConfirmName !== activeBrandData?.name}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
          >
            Excluir Permanentemente
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog - Criar Novo Link Público */}
      <PlatformDialog 
        open={createTokenDialog} 
        onClose={closeCreateTokenDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <PlatformDialogTitle
          title="Criar Link Público"
          subtitle="Gere um link compartilhável para o dashboard desta marca"
          icon={<LinkIcon />}
          onClose={closeCreateTokenDialog}
        />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label="Nome do Link"
              placeholder="Ex: Dashboard para Investidores"
              value={newTokenData.name}
              onChange={(e) => setNewTokenData({ ...newTokenData, name: e.target.value })}
              required
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="Descrição (opcional)"
              placeholder="Ex: Link para compartilhar com investidores"
              value={newTokenData.description}
              onChange={(e) => setNewTokenData({ ...newTokenData, description: e.target.value })}
              multiline
              rows={2}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="Expira em (opcional)"
              type="date"
              value={newTokenData.expiresAt}
              onChange={(e) => setNewTokenData({ ...newTokenData, expiresAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Deixe vazio para não expirar"
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="PIN de acesso (opcional)"
              placeholder="Ex: 1234 ou cliente2026"
              value={newTokenData.pin}
              onChange={(e) => setNewTokenData({ ...newTokenData, pin: e.target.value })}
              type="text"
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                inputMode: 'text',
                spellCheck: 'false'
              }}
              helperText="Se preencher, quem abrir o link precisara informar este PIN."
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button 
            onClick={closeCreateTokenDialog}
            sx={{ fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateToken}
            disabled={tokensLoading || !newTokenData.name.trim()}
            startIcon={tokensLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{ fontWeight: 600, borderRadius: 2, px: 3 }}
          >
            {tokensLoading ? 'Criando...' : 'Criar Link'}
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Modal - Confirmar Exclusão de Token */}
      <ConfirmDialog
        open={deleteTokenDialogOpen}
        title="Excluir Link"
        message={`Tem certeza que deseja excluir o link "${deleteTokenName}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        onConfirm={confirmDeleteToken}
        onCancel={() => setDeleteTokenDialogOpen(false)}
      />

      {/* Modal - Confirmar Regeneração de Token */}
      <PlatformDialog
        open={regenerateDialogOpen}
        onClose={() => setRegenerateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <PlatformDialogTitle
          title="Regenerar Link"
          subtitle="O link antigo deixará de funcionar."
          icon={<RefreshIcon />}
          onClose={() => setRegenerateDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Ao regenerar, qualquer pessoa com o link antigo perderá o acesso.
            Um novo link será gerado com a mesma configuração.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button onClick={() => setRegenerateDialogOpen(false)} sx={{ fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmRegenerateToken}
            startIcon={<RefreshIcon />}
            sx={{ fontWeight: 600, borderRadius: 2 }}
          >
            Regenerar
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BrandSettingsPage;
